// ─── Workshop Truck Availability Tracker ───
// AllTrucks view – Full fleet register with search, sort, filter, pagination
// IASL Technical Academy / Lafarge Driving Institute, Nigeria

import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import {
  Search,
  X,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  Edit2,
  CheckCircle,
  Wrench,
  SearchX,
} from 'lucide-react'

import type { Truck, ToastMessage, WorkshopStatus } from '@/types'
import { useTrucks } from '@/hooks/useTrucks'
import StatusBadge from '@/components/ui/StatusBadge'
import TruckDetailModal from '@/components/modals/TruckDetailModal'
import StatusUpdateModal from '@/components/modals/StatusUpdateModal'
import { ALL_STATUSES, formatDateTime, calcDaysInWorkshop, getDaysColor, normalizeMake } from '@/utils/helpers'

// ─── Props ──────────────────────────────────────────────────

interface AllTrucksProps {
  addToast: (type: ToastMessage['type'], message: string) => void
}

// ─── Client colour badges ───────────────────────────────────

const CLIENT_COLORS: Record<string, string> = {
  LAPW: 'bg-blue-100 text-blue-800',
  TSL:  'bg-green-100 text-green-800',
  LAPE: 'bg-purple-100 text-purple-800',
  TNP:  'bg-orange-100 text-orange-800',
}

function clientBadgeClass(name: string): string {
  const key = Object.keys(CLIENT_COLORS).find((k) =>
    name.toUpperCase().includes(k),
  )
  return key ? CLIENT_COLORS[key] : 'bg-gray-100 text-gray-700'
}

// ─── Known clients (for filter dropdown) ────────────────────

const KNOWN_CLIENTS = ['LAPW', 'TSL', 'LAPE', 'TNP']

// ─── Sort types ─────────────────────────────────────────────

type SortField = 'registration' | 'client_name' | 'workshop_status' | 'last_status_update'
type SortDir = 'asc' | 'desc'

// ─── Page sizes ─────────────────────────────────────────────

const PAGE_SIZES = [25, 50, 100]

// ─── Component ──────────────────────────────────────────────

export default function AllTrucks({ addToast }: AllTrucksProps) {
  // ── API filters (server-side) ─────────────────────────────
  const [apiClient, setApiClient] = useState<string>('')
  const [apiStatus, setApiStatus] = useState<string>('')
  const [workshopToggle, setWorkshopToggle] = useState<'' | 'true' | 'false'>('')

  const { trucks, isLoading, refresh, setFilters } = useTrucks()

  // Sync API filters to useTrucks hook
  useEffect(() => {
    setFilters({
      client: apiClient || undefined,
      status: apiStatus || undefined,
      in_workshop: workshopToggle || undefined,
    })
  }, [apiClient, apiStatus, workshopToggle, setFilters])

  // ── Client-side search ────────────────────────────────────
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value)
    }, 300)
  }, [])

  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [])

  // ── Sort state ────────────────────────────────────────────
  const [sortField, setSortField] = useState<SortField>('registration')
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDir('asc')
    }
  }

  // ── Pagination state ──────────────────────────────────────
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  // Reset page when filters/search change
  useEffect(() => { setPage(1) }, [debouncedSearch, apiClient, apiStatus, workshopToggle, pageSize])

  // ── Filter + search + sort pipeline ───────────────────────
  const processedData = useMemo(() => {
    let result = [...trucks]

    // Client-side text search
    if (debouncedSearch.trim()) {
      const q = debouncedSearch.toLowerCase()
      result = result.filter(
        (t) =>
          t.registration?.toLowerCase().includes(q) ||
          t.fleet_number?.toLowerCase().includes(q) ||
          normalizeMake(t.make).toLowerCase().includes(q) ||
          t.client_name?.toLowerCase().includes(q),
      )
    }

    // Sort
    result.sort((a, b) => {
      const aVal = (a[sortField] ?? '') as string
      const bVal = (b[sortField] ?? '') as string
      const cmp = aVal.localeCompare(bVal)
      return sortDir === 'asc' ? cmp : -cmp
    })

    return result
  }, [trucks, debouncedSearch, sortField, sortDir])

  // ── Pagination calculations ───────────────────────────────
  const totalFiltered = processedData.length
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize))
  const safePage = Math.min(page, totalPages)
  const startIdx = (safePage - 1) * pageSize
  const endIdx = Math.min(startIdx + pageSize, totalFiltered)
  const pageData = processedData.slice(startIdx, endIdx)

  // ── Any filter active? ────────────────────────────────────
  const hasActiveFilters = !!(searchInput || apiClient || apiStatus || workshopToggle)

  const clearFilters = () => {
    setSearchInput('')
    setDebouncedSearch('')
    setApiClient('')
    setApiStatus('')
    setWorkshopToggle('')
  }

  // ── Modal state ───────────────────────────────────────────
  const [selectedTruck, setSelectedTruck] = useState<Truck | null>(null)
  const [modalMode, setModalMode] = useState<'detail' | 'edit' | null>(null)

  const openDetail = (truck: Truck) => { setSelectedTruck(truck); setModalMode('detail') }
  const openEdit   = (truck: Truck) => { setSelectedTruck(truck); setModalMode('edit') }
  const closeModal = () => { setSelectedTruck(null); setModalMode(null) }

  // ── Page number buttons ───────────────────────────────────
  const pageButtons = useMemo(() => {
    const pages: (number | '...')[] = []
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i)
    } else {
      pages.push(1)
      if (safePage > 3) pages.push('...')
      const start = Math.max(2, safePage - 1)
      const end = Math.min(totalPages - 1, safePage + 1)
      for (let i = start; i <= end; i++) pages.push(i)
      if (safePage < totalPages - 2) pages.push('...')
      pages.push(totalPages)
    }
    return pages
  }, [safePage, totalPages])

  // ── Sort header helper ────────────────────────────────────
  const SortHeader = ({ field, label, className = '' }: { field: SortField; label: string; className?: string }) => (
    <th
      className={`text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3 cursor-pointer select-none hover:text-gray-700 transition-colors ${className}`}
      onClick={() => toggleSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        <span className="flex flex-col leading-none">
          <ChevronUp
            size={10}
            className={sortField === field && sortDir === 'asc' ? 'text-blue-600' : 'text-gray-300'}
          />
          <ChevronDown
            size={10}
            className={sortField === field && sortDir === 'desc' ? 'text-blue-600' : 'text-gray-300'}
          />
        </span>
      </div>
    </th>
  )

  // ═════════════════════════════════════════════════════════
  // RENDER
  // ═════════════════════════════════════════════════════════

  return (
    <div>
      {/* ═══ PAGE HEADER ═════════════════════════════════════ */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Fleet Register</h1>
          <p className="text-sm text-gray-500">
            Complete asset registry across all client groups
          </p>
        </div>
        <span className="bg-blue-50 text-blue-700 text-sm px-3 py-1 rounded-full font-medium">
          Showing {totalFiltered} of {trucks.length} trucks
        </span>
      </div>

      {/* ═══ FILTER BAR ══════════════════════════════════════ */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
        <div className="flex gap-3 flex-wrap items-center">
          {/* Search */}
          <div className="relative flex-1 min-w-[240px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search fleet #, make, client..."
              className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm
                         focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Client filter */}
          <select
            value={apiClient}
            onChange={(e) => setApiClient(e.target.value)}
            className="min-w-[160px] border border-gray-300 rounded-lg px-3 py-2.5 text-sm
                       focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Clients</option>
            {KNOWN_CLIENTS.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>

          {/* Status filter */}
          <select
            value={apiStatus}
            onChange={(e) => setApiStatus(e.target.value)}
            className="min-w-[200px] border border-gray-300 rounded-lg px-3 py-2.5 text-sm
                       focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Statuses</option>
            {ALL_STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>

          {/* Workshop toggle group */}
          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            {([
              { value: '' as const,      label: 'All' },
              { value: 'true' as const,  label: 'In Workshop' },
              { value: 'false' as const, label: 'Active Only' },
            ]).map(({ value, label }) => (
              <button
                key={value}
                onClick={() => setWorkshopToggle(value)}
                className={`px-3 py-2 text-sm font-medium transition-colors
                  ${workshopToggle === value
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Clear filters */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 text-gray-500 hover:text-red-500 text-sm
                         transition-colors px-2 py-2"
            >
              Clear <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* ═══ TABLE ═══════════════════════════════════════════ */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3 w-10">
                  #
                </th>
                <SortHeader field="registration" label="Fleet #" />
                <SortHeader field="client_name" label="Client" />
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">
                  Make / Model
                </th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">
                  Workshop
                </th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">
                  SLA
                </th>
                <SortHeader field="workshop_status" label="Status" />
                <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">
                  Avail
                </th>
                <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">
                  Days in W/S
                </th>
                <SortHeader field="last_status_update" label="Last Updated" />
                <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {/* LOADING STATE */}
              {isLoading && trucks.length === 0 && (
                <>
                  {Array.from({ length: 8 }, (_, i) => (
                    <tr key={`skel-${i}`} className="border-b border-gray-100 animate-pulse">
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-6" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-20" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-14" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-14" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-24" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-16" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-14" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-24" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-6 mx-auto" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-14 mx-auto" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-28" /></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-12 mx-auto" /></td>
                    </tr>
                  ))}
                </>
              )}

              {/* EMPTY STATE */}
              {!isLoading && pageData.length === 0 && (
                <tr>
                  <td colSpan={12} className="py-16 text-center">
                    <SearchX size={48} className="mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-600 font-medium mb-1">
                      No trucks match your search criteria
                    </p>
                    <p className="text-gray-400 text-sm mb-4">
                      Try adjusting your filters or clearing the search
                    </p>
                    {hasActiveFilters && (
                      <button
                        onClick={clearFilters}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm
                                   hover:bg-blue-700 transition-colors"
                      >
                        Clear Filters
                      </button>
                    )}
                  </td>
                </tr>
              )}

              {/* DATA ROWS */}
              {!isLoading && pageData.map((truck, idx) => (
                <tr
                  key={truck.asset_id}
                  className="hover:bg-gray-50 border-b border-gray-100 transition-colors"
                >
                  {/* # */}
                  <td className="px-4 py-3 text-sm text-gray-400">
                    {startIdx + idx + 1}
                  </td>

                  {/* Fleet # */}
                  <td className="px-4 py-3 text-sm font-semibold text-gray-800">
                    {truck.registration}
                  </td>

                  {/* Client */}
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${clientBadgeClass(truck.client_name)}`}>
                      {truck.client_name}
                    </span>
                  </td>

                  {/* Make / Model */}
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                    {[normalizeMake(truck.make), truck.model].filter(Boolean).join(' ')}
                  </td>

                  {/* Workshop */}
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {truck.workshop_location || '—'}
                  </td>

                  {/* SLA */}
                  <td className="px-4 py-3">
                    {truck.sla_status ? (
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        truck.sla_status === 'SLA'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {truck.sla_status}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3">
                    <StatusBadge status={truck.workshop_status as WorkshopStatus} size="sm" />
                  </td>

                  {/* Available */}
                  <td className="px-4 py-3 text-center">
                    {truck.is_available === 1 ? (
                      <CheckCircle size={16} className="text-green-500 mx-auto" />
                    ) : (
                      <Wrench size={16} className="text-amber-500 mx-auto" />
                    )}
                  </td>

                  {/* Days in Workshop */}
                  <td className="px-4 py-3 text-center">
                    {truck.is_available === 0 ? (() => {
                      const days = calcDaysInWorkshop(truck.workshop_entry_date)
                      return days !== null ? (
                        <span className={`text-sm tabular-nums ${getDaysColor(days)}`}>
                          {days}d
                        </span>
                      ) : <span className="text-xs text-gray-400">—</span>
                    })() : <span className="text-xs text-gray-400">—</span>}
                  </td>

                  {/* Last Updated */}
                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                    {formatDateTime(truck.last_status_update)}
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3">
                    <div className="flex gap-1 justify-center">
                      <button
                        onClick={() => openDetail(truck)}
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-blue-600 transition-colors"
                        title="View details"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => openEdit(truck)}
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-amber-600 transition-colors"
                        title="Update status"
                      >
                        <Edit2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ═══ PAGINATION ════════════════════════════════════ */}
        {totalFiltered > 0 && (
          <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between flex-wrap gap-3">
            {/* Left: showing info */}
            <span className="text-sm text-gray-600">
              Showing <strong>{startIdx + 1}</strong>–<strong>{endIdx}</strong> of{' '}
              <strong>{totalFiltered}</strong> trucks
            </span>

            {/* Right: controls */}
            <div className="flex items-center gap-3">
              {/* Page size select */}
              <div className="flex items-center gap-1.5 text-sm text-gray-600">
                <span>Rows:</span>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="border border-gray-300 rounded-md px-2 py-1 text-sm
                             focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {PAGE_SIZES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              {/* Previous */}
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={safePage <= 1}
                className="p-1.5 rounded-md border border-gray-300 hover:bg-gray-50 transition-colors
                           disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={16} />
              </button>

              {/* Page numbers */}
              <div className="flex gap-1">
                {pageButtons.map((p, i) =>
                  p === '...' ? (
                    <span key={`ell-${i}`} className="px-2 py-1 text-sm text-gray-400">
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`min-w-[32px] h-8 rounded-md text-sm font-medium transition-colors
                        ${safePage === p
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                        }`}
                    >
                      {p}
                    </button>
                  ),
                )}
              </div>

              {/* Next */}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage >= totalPages}
                className="p-1.5 rounded-md border border-gray-300 hover:bg-gray-50 transition-colors
                           disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ═══ MODALS ═════════════════════════════════════════ */}
      {modalMode === 'detail' && selectedTruck && (
        <TruckDetailModal
          truck={selectedTruck}
          onClose={closeModal}
          onEdit={() => setModalMode('edit')}
        />
      )}

      {modalMode === 'edit' && selectedTruck && (
        <StatusUpdateModal
          truck={selectedTruck}
          onClose={closeModal}
          onSuccess={async () => {
            refresh()
            addToast('success', `Status updated for ${selectedTruck.registration}`)
            closeModal()
          }}
        />
      )}
    </div>
  )
}
